import React, { Component } from 'react'
import { Alert } from 'antd'
import { injectIntl, FormattedMessage } from 'react-intl'
import { withRouter } from 'react-router-dom'
import { withApollo } from '@apollo/client/react/hoc'
import gql from 'graphql-tag'
import Loading from '../../components/LoadingPage'
import { Query } from '@apollo/client/react/components'
import { ThirdPartyDeviceDetailsQuery } from '../thirdPartyDevices/Queries'
import AppLayout from '../../components/PageLayout'
import * as Utils from '../../components/Utils'

const specificErrorCodes = [
	'THIRD_PARTY_DEVICES_NOT_ENABLED',
	'MAX_DEVICES_REACHED'
]

class SonosPairDevice extends Component {
	/**
	 * When the component is mount, we check if the 3rd party device already exists.
	 * If no, we trigger the setup and display instructions to the user.
	 * If yes, we display a success message and redirect the user to the Sonos app.
	 * In both cases, we use the callbackPath query param to redirect the user to the Sonos app after the process.
	 */
	async componentDidMount() {
		try {
			const history = this.props.history
			const device = await this.fetchThirdPartyDevice(
				this.props.computedMatch.params.devId
			)
			this.setState({ device })

			if (device) {
				// we display a success message, then the result page will redirect to the sonos app
				this.goToResultPage('ALREADY_PAIRED_SUCCESS')
			} else {
				await this.setupSonosDevice()
			}
		} catch (e) {
			this.setState({ loading: false })
			const errorMessage = e.graphQLErrors?.[0]?.message
			const isSpecificError = specificErrorCodes.includes(errorMessage)
			if (isSpecificError) {
				this.goToResultPage(`${errorMessage}_ERROR`)
			} else {
				this.goToResultPage('UNKNOWN_ERROR')
			}
		}
	}

	state = {
		error: null,
		// we use props.computedMatch since we are using withRouter HOC
		devId: this.props.computedMatch.params.devId,
		uuid: this.props.computedMatch.params.uuid,

		timeZone: new URLSearchParams(this.props.location.search).get(
			'timeZone'
		),
		callbackPath: new URLSearchParams(this.props.location.search).get(
			'callbackPath'
		)
	}

	setupSonosDevice = async () => {
		const { client, history } = this.props

		try {
			this.setState({ loading: true })

			// This request will create the 3rd party device with a default team and program
			// on success we display a message that ask the user to contact the support to finalize
			// the configuration.
			const r = await client.mutate({
				variables: {
					deviceId: this.state.devId,
					uuid: this.state.uuid,
					timeZone: this.state.timeZone
				},
				mutation: gql`
					mutation setupSonosDevice(
						$deviceId: String!
						$uuid: String!
						$timeZone: String
					) {
						setupSonosDevice(
							deviceId: $deviceId
							uuid: $uuid
							timeZone: $timeZone
						) {
							success
							deviceId
							message
						}
					}
				`
			})

			this.setState({ loading: false })
			Utils.parseMutationResponse(r)

			const res = r.data.setupSonosDevice
			if (res && res.success) {
				this.goToResultPage('FIRST_PAIRING_SUCCESS')
			}
		} catch (e) {
			this.setState({ loading: false })
			const errorMessage = e.graphQLErrors?.[0]?.message
			const isSpecificError = specificErrorCodes.includes(errorMessage)
			if (isSpecificError) {
				this.goToResultPage(`${errorMessage}_ERROR`)
			} else {
				Utils.displayError(e)
				this.goToResultPage('UNKNOWN_ERROR')
			}
		}
	}

	render() {
		const { intl, history } = this.props
		const { loading, device } = this.state

		let content
		if (loading) {
			content = <Loading />
		} else if (!device) {
			content = (
				<React.Fragment>
					<h3>
						<FormattedMessage id="devices.pair.sonosTitle" />
					</h3>
				</React.Fragment>
			)
		} else {
			content = null
		}

		return (
			<AppLayout hideNav>
				<div style={styles.body}>{content}</div>
			</AppLayout>
		)
	}

	fetchThirdPartyDevice = async (deviceId) => {
		const { client } = this.props

		try {
			this.setState({ loading: true })
			const result = await client.query({
				variables: { id: deviceId },
				query: ThirdPartyDeviceDetailsQuery
			})

			this.setState({ loading: false })

			return result.data.thirdPartyDeviceById
		} catch (e) {
			this.setState({ loading: false })
			Utils.displayError(e)
			throw e
		}
	}

	goToResultPage = (code) => {
		const params = new URLSearchParams({})

		if (this.state.callbackPath) {
			params.set('callbackPath', this.state.callbackPath)
		}

		const { history } = this.props
		history.push({
			pathname: `/devices/sonos/pairing_result/${code}/`,
			search: `?${params}`
		})
	}
}

const styles = {
	body: {
		padding: 20,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		flexDirection: 'column'
	}
}

export default withRouter(withApollo(injectIntl(SonosPairDevice)))
